import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Badge, Button, Grid } from '@mui/material';

import WorkQueues, { CENTRAL_FILL_MODE } from 'enums/workQueue.enum';
import { listCentralFillQueueByPage } from 'store/thunks/workQueueThunks';
import useRoles from 'common/hooks/useRoles';
import { workQueueActions } from 'store/slices/workQueueSlice';
import { useWorkQueueTabSwitch } from '../contexts/WorkQueueTabSwitchContext';
import {
  FORCE_RETURN_ELIGIBLE_STATUSES,
  RECALLABLE_STATUSES,
} from '../constants/centralFillEligibility';

import CentralFillQueueTable from './CentralFillQueueTable';
import CentralFillQueueModeSwitch from './CentralFillQueueModeSwitch';
import RelayOrdersTable from './RelayOrdersTable';
import RelayOrdersFilterButton from './RelayOrdersFilterButton';
import RelayOrdersFilterStatusBar from './RelayOrdersFilterStatusBar';
import RelayOrderDetailsDialog from './RelayOrderDetailsDialog';
import CentralFillQueueFilterButton from './CentralFillQueueFilterButton';
import CentralFillQueueFilterStatusBar from './CentralFillQueueFilterStatusBar';
import RecallCentralFillOrdersModal from './RecallCentralFillOrdersModal';
import ForceReturnCentralFillOrdersModal from './ForceReturnCentralFillOrdersModal';

// Recall is gated behind a build-time env flag (CRA REACT_APP_*). It's enabled ONLY
// when REACT_APP_RECALL_ENABLED is exactly 'true'; absent/null/any other value falls
// back to false, so the Recall action (button + modal) stays hidden by default.
const RECALL_FEATURE_ENABLED = process.env.REACT_APP_RECALL_ENABLED === 'true';

// A row is selectable only if it is eligible for an AVAILABLE bulk action. When recall
// is gated off, recall-only statuses are NOT selectable — otherwise a user could check a
// row that has no visible action, and the Force Return count would not match the
// selection.
const SELECTABLE_STATUSES = new Set([
  ...(RECALL_FEATURE_ENABLED ? RECALLABLE_STATUSES : []),
  ...FORCE_RETURN_ELIGIBLE_STATUSES,
]);

const CentralFillQueue = () => {
  const dispatch = useDispatch();
  const preset = useSelector(({ workQueue }) => workQueue.preset);
  const { currentPage, pages } = useSelector(
    ({ workQueue }) => workQueue.compoundingQueue,
    shallowEqual
  );
  const { isTabSwitch } = useWorkQueueTabSwitch();
  const { viewRelayedOrders } = useRoles();
  const storedMode = useSelector(({ workQueue }) => workQueue.compoundingQueue.mode);

  /**
   * POM-6099. An explicit ALLOW-LIST of the one value that means relay, AND a role check on the
   * CONTENT rather than only on the switch.
   *
   * The role check is not belt-and-braces, but NOT for the reason it is tempting to give: `mode`
   * is NOT in workQueueSlice's redux-persist whitelist (only preset / previousPreset /
   * showLimitedTabs are), so it does not survive a reload. What it does survive is a SIGN-OUT:
   * `resetAppState` in store/thunks/authThunks.js resets auth, patient, partner, pharmacy and
   * orders but NOT workQueue, so after a sign-out without a page reload the mode is still RELAY
   * and relayQueue still holds the previous sys-admin's rows. Gating only the switch would show
   * those to whoever signed in next.
   *
   * Written as `=== RELAY` rather than `!== CENTRAL_FILL` so an unrecognised or absent value
   * falls back to central fill -- the view almost everyone is here for -- not to the admin-only
   * one.
   */
  const isRelayMode = viewRelayedOrders && storedMode === CENTRAL_FILL_MODE.RELAY;

  // Selected eligible line items on the current page.
  const [selectedLineItemIds, setSelectedLineItemIds] = useState([]);
  // Which relayed order's details are open, if any. Held here rather than in the table so the
  // dialog survives a page change underneath it.
  const [detailsRelayOrderId, setDetailsRelayOrderId] = useState(null);
  const [recallModalOpen, setRecallModalOpen] = useState(false);
  const [forceReturnModalOpen, setForceReturnModalOpen] = useState(false);

  // Full row objects for the selected line items — the confirmation modals
  // list order / patient / drug context, not just ids.
  const selectedOrders = useMemo(
    () => (pages[currentPage] || []).filter((row) => selectedLineItemIds.includes(row.lineItemId)),
    [pages, currentPage, selectedLineItemIds]
  );

  // Per-action subset: a single selection can mix recall- and force-return-
  // eligible rows (e.g. after Select All). Each action operates only on the rows
  // in the selection that are eligible for it — so its button is enabled when at
  // least one such row is selected, and its modal lists exactly those rows.
  // RECALL_FAILED is eligible for both, so it appears under whichever action the
  // operator confirms.
  const recallOrders = useMemo(
    () => selectedOrders.filter((order) => RECALLABLE_STATUSES.has(order.status)),
    [selectedOrders]
  );
  const forceReturnOrders = useMemo(
    () => selectedOrders.filter((order) => FORCE_RETURN_ELIGIBLE_STATUSES.has(order.status)),
    [selectedOrders]
  );
  const canRecall = recallOrders.length > 0;
  const canForceReturn = forceReturnOrders.length > 0;

  /**
   * POM-6099. TWO triggers, because this queue has two axes and `isTabSwitch` only knows about
   * one of them.
   *
   * Every other queue in this module loads on the tab switch alone, and that is sufficient for
   * them: arriving at the tab is the only way their content comes on screen. Central fill is the
   * only one with a second axis -- the relay mode this ticket added -- and toggling it is NOT a
   * tab switch. `isTabSwitch` compares `preset` against its previous value, and `preset` stays
   * COMPOUNDING_QUEUE across a mode toggle, so it stays false. Leaving relay would therefore show
   * the central fill table with no fetch behind it.
   *
   * That is usually STALE (rows from before the detour, with anything that changed meanwhile
   * invisible) and sometimes EMPTY, which is worse: if the tab was first entered in relay mode,
   * the central fill pages were never loaded at all. That path is reachable -- `mode` is not
   * persisted, but `resetAppState` (store/thunks/authThunks.js) does not reset workQueue either,
   * so a sign-out without a page reload carries RELAY into the next session.
   *
   * Keyed on the TRANSITION out of relay rather than on `!isRelayMode`, which would put a queue
   * fetch on every render of the default view.
   */
  const wasRelayModeRef = useRef(isRelayMode);

  useEffect(() => {
    const leftRelayMode = wasRelayModeRef.current && !isRelayMode;
    wasRelayModeRef.current = isRelayMode;

    // Not while relay is on screen: this would refill the central fill pages behind a view that
    // is not showing them, and spend a query nobody asked for.
    if (!isRelayMode && (leftRelayMode || isTabSwitch(WorkQueues.COMPOUNDING_QUEUE))) {
      dispatch(listCentralFillQueueByPage({ page: currentPage }));
    }
  }, [dispatch, currentPage, isTabSwitch, isRelayMode]);

  // A selection means "these central fill line items". Carrying it across a mode change would
  // leave Route to Default armed with rows that are no longer on screen.
  useEffect(() => {
    setSelectedLineItemIds([]);
  }, [isRelayMode]);

  const handleModeChange = useCallback(
    (mode) => dispatch(workQueueActions.updateCentralFillQueueMode({ mode })),
    [dispatch]
  );

  // After processing — clear selection and refresh the queue so updated
  // statuses (RECALLED / RECALL_FAILED / FORCE_RETURNED) are visible.
  const handleProcessed = useCallback(() => {
    setSelectedLineItemIds([]);
    dispatch(listCentralFillQueueByPage({ page: currentPage }));
  }, [dispatch, currentPage]);

  if (preset !== WorkQueues.COMPOUNDING_QUEUE) {
    return null;
  }

  if (isRelayMode) {
    return (
      <Grid container sx={{ py: 3 }} direction='column' gap={2}>
        <Grid item container justifyContent='space-between' alignItems='center'>
          <Grid item>
            <CentralFillQueueModeSwitch
              mode={CENTRAL_FILL_MODE.RELAY}
              onChange={handleModeChange}
            />
          </Grid>
          <Grid item>
            <RelayOrdersFilterButton />
          </Grid>
        </Grid>
        <Grid item>
          <RelayOrdersFilterStatusBar />
        </Grid>
        <Grid item>
          <RelayOrdersTable onOpenDetails={setDetailsRelayOrderId} />
        </Grid>
        {/* Keyed by order id so opening a different order remounts the dialog rather than
            reusing a component still holding the previous patient's details. */}
        <RelayOrderDetailsDialog
          key={detailsRelayOrderId}
          relayOrderId={detailsRelayOrderId}
          onClose={() => setDetailsRelayOrderId(null)}
        />
      </Grid>
    );
  }

  // With the mode switch, the actions sit at the right end of the switch's line and the filter
  // chips drop below it; without it, the chips keep the left of that line, as before.
  const actionItemSx = viewRelayedOrders ? undefined : { mt: { sm: '25px', md: '25px' } };

  return (
    <Grid container sx={{ py: 3 }} justifyContent='space-between' alignItems='center'>
      {viewRelayedOrders ? (
        <Grid item>
          <CentralFillQueueModeSwitch
            mode={CENTRAL_FILL_MODE.CENTRAL_FILL}
            onChange={handleModeChange}
          />
        </Grid>
      ) : (
        <Grid item sx={{ minHeight: 40, flexGrow: 1 }}>
          <CentralFillQueueFilterStatusBar />
        </Grid>
      )}
      <Grid item>
        <Grid container gap={1}>
          {/* Recall is gated off (RECALL_FEATURE_ENABLED) — not shown to anyone until
              the feature ships. When enabled, it shows only for a recall-eligible
              selection; the badge shows how many will be recalled. */}
          {RECALL_FEATURE_ENABLED && canRecall && (
            <Grid item sx={actionItemSx}>
              <Badge color='error' badgeContent={recallOrders.length} sx={{ mx: 0.5 }}>
                <Button
                  variant='contained'
                  color='primary'
                  sx={{ height: 33 }}
                  onClick={() => setRecallModalOpen(true)}
                >
                  Recall
                </Button>
              </Badge>
            </Grid>
          )}
          <Grid item sx={actionItemSx}>
            {/* POM-5455: Force Return — always visible, enabled when the
                selection contains at least one force-return-eligible order.
                Badge shows how many will be force returned. */}
            <Badge color='error' badgeContent={forceReturnOrders.length} sx={{ mx: 0.5 }}>
              <Button
                variant='contained'
                color='primary'
                sx={{ height: 33 }}
                disabled={!canForceReturn}
                onClick={() => setForceReturnModalOpen(true)}
              >
                Route to Default
              </Button>
            </Badge>
          </Grid>
          <Grid item sx={actionItemSx}>
            <CentralFillQueueFilterButton />
          </Grid>
        </Grid>
      </Grid>
      {viewRelayedOrders && (
        <Grid item xs={12} sx={{ pt: 1 }}>
          <CentralFillQueueFilterStatusBar />
        </Grid>
      )}
      <CentralFillQueueTable
        selectedLineItemIds={selectedLineItemIds}
        onSelectionChange={setSelectedLineItemIds}
        eligibleStatuses={SELECTABLE_STATUSES}
        selectAllAriaLabel='select all eligible orders'
        ineligibleTooltip='Only failed/stuck or recallable orders can be selected'
      />
      {RECALL_FEATURE_ENABLED && (
        <RecallCentralFillOrdersModal
          open={recallModalOpen}
          orders={recallOrders}
          onClose={() => setRecallModalOpen(false)}
          onProcessed={handleProcessed}
        />
      )}
      <ForceReturnCentralFillOrdersModal
        open={forceReturnModalOpen}
        orders={forceReturnOrders}
        onClose={() => setForceReturnModalOpen(false)}
        onProcessed={handleProcessed}
      />
    </Grid>
  );
};

export default CentralFillQueue;
